import {defineSlotRecipe} from '@chakra-ui/react3'

/**
 * recipe for Hero:
 * slots:
 * - container
 * - assetContainer
 * - darkenLayerBottom
 * - darkenLayerLeft
 * - textContainer
 * - heading
 * - copy
 * variants:
 * - textPosition: bottom-left | center-left | center-center
 * - darken: 0% | 40% | 80%
 * compoundVariants:
 * - if textPosition is bottom-left or center-left and darken is 40% or 80%, darkenLayerLeft is gradient.left.darken-40
 * - if textPosition is center-center and darken is 40% or 80%, darkenLayerLeft is gradient.left.darken-80
 * defaultVariants:
 * - textPosition: center-center
 * - darken: 0%
 */
export default defineSlotRecipe({
    slots: [
        'container',
        'widthContainer',
        'assetContainer',
        'clipContainer',
        'darkenLayerBottom',
        'darkenLayerLeft',
        'textContainer',
        'heading',
        'copy'
    ],
    base: {
        container: {
            position: 'relative',
            height: 'calc(100vh - var(--header-clearance-block-size))',
            overflow: 'hidden'
        },
        assetContainer: {
            position: 'absolute',
            inset: 0,
            overflow: 'hidden'
        },
        clipContainer: {
            position: 'absolute',
            left: '0',
            right: '0',
            bottom: '-9%',
            h: '12%',
            pointerEvents: 'none',
            clipPath: {base: 'ellipse(70% 50% at 50% 60%)', s: 'ellipse(58% 50% at 50% 50%)'}
        },
        darkenLayerBottom: {
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none'
        },
        darkenLayerLeft: {
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none'
        },
        widthContainer: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: {base: 'spacing-24', s: 'spacing-40'},
            height: '100%',
            px: {base: 'spacing-24', s: 'spacing-90'},
            paddingBlockStart: {base: 'spacing-48', s: 'spacing-80'},
            paddingBlockEnd: {base: 'spacing-24', s: 'spacing-80'}
        },
        textContainer: {
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            rowGap: 'spacing-8',
            maxW: 'module-content-max-width'
        }
    },
    variants: {
        textPosition: {
            'bottom-left': {
                widthContainer: {
                    justifyContent: 'flex-end',
                    alignItems: 'flex-start'
                },
                textContainer: {
                    justifyContent: 'flex-end',
                    alignItems: 'flex-start'
                },
                heading: {
                    alignment: 'left'
                },
                copy: {
                    alignment: 'left'
                }
            },
            'center-left': {
                widthContainer: {
                    justifyContent: 'center',
                    alignItems: 'flex-start'
                },
                textContainer: {
                    justifyContent: 'center',
                    alignItems: 'flex-start'
                },
                heading: {
                    alignment: 'left'
                },
                copy: {
                    alignment: 'left'
                }
            },
            'center-center': {
                widthContainer: {
                    justifyContent: 'center',
                    alignItems: 'center'
                },
                textContainer: {
                    justifyContent: 'center',
                    alignItems: 'center'
                },
                heading: {
                    alignment: 'center'
                },
                copy: {
                    alignment: 'center'
                }
            }
        },
        darken: {
            '0%': {
                darkenLayerBottom: {
                    bg: 'transparent'
                },
                darkenLayerLeft: {
                    bg: 'transparent'
                }
            },
            '40%': {
                darkenLayerBottom: {
                    bg: 'gradient.bottom.darken-40'
                }
            },
            '80%': {
                darkenLayerBottom: {
                    bg: 'gradient.bottom.darken-80'
                }
            }
        },
        background: {
            default: {
                clipContainer: {bg: 'background.section.default'}
            },
            subtle: {
                clipContainer: {
                    bg: 'background.section.subtle'
                }
            },
            strong: {
                clipContainer: {
                    bg: 'background.section.strong'
                }
            },
            inverse: {
                clipContainer: {
                    bg: 'background.section.strong'
                }
            }
        },
        hasVideoControl: {
            true: {
                widthContainer: {
                    paddingBlockEnd: 'spacing-80'
                }
            },
            false: {}
        }
    },
    compoundVariants: [
        {
            textPosition: ['bottom-left', 'center-left'],
            darken: '40%',
            css: {
                darkenLayerLeft: {
                    bg: 'gradient.left.darken-40'
                }
            }
        },
        {
            textPosition: ['bottom-left', 'center-left'],
            darken: '80%',
            css: {
                darkenLayerLeft: {
                    bg: 'gradient.left.darken-40'
                }
            }
        },
        {
            textPosition: 'center-center',
            darken: '40%',
            css: {
                darkenLayerLeft: {
                    bg: 'gradient.left.darken-80'
                }
            }
        },
        {
            textPosition: 'center-center',
            darken: '80%',
            css: {
                darkenLayerLeft: {
                    bg: 'gradient.left.darken-80'
                }
            }
        }
    ],
    defaultVariants: {
        textPosition: 'center-center',
        darken: '0%',
        background: 'default'
    }
})
